import { ActionService, type FormEditDraftActionCommand } from '@acme/api';
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  merge,
  sample,
} from 'effector';

export const confirmRequested = createEvent<{ actionId: string }>();
export const rejectRequested = createEvent<{
  actionId: string;
  comment?: string;
}>();
export const deleteRequested = createEvent<{ actionId: string }>();
export const reworkRequested = createEvent<{
  actionId: string;
  comment: string;
}>();

export const actionSucceeded = createEvent();

export const editApplicationRequested = createEvent<{
  actionId: string;
  applicationId: string;
  contractUniqueNumber: string;
  tradeAgreementNumber: string;
  files: File[];
}>();
export const editDraftRequested = createEvent<{
  actionId: string;
  requestBody: FormEditDraftActionCommand;
}>();
export const sendForReviewRequested = createEvent<{ actionId: string }>();
export const signRequested = createEvent<{ actionId: string }>();

export type FormAgentInfoFormData = {
  commissionAuthorizedOrganizationRUB?: number | string;
  commissionAgentRUB?: number | string;
  agentCryptoAddress?: string;
  agentWalletNetId?: number | string;
  cryptoCurrencyId?: number | string;
  cryptoAmount?: number | string;
  organizationName?: string;
  senderCountryId?: number | string;
  bankName?: string;
  files: File[];
};

export const formAgentInfoRequested = createEvent<{
  actionId: string;
  data: FormAgentInfoFormData;
}>();

export const counterpartyOfferRequested = createEvent<{
  actionId: string;
  sign?: string;
  commissionAuthorizedOrganizationRUB: number;
  files: File[];
}>();

export const applicationActionSucceeded = createEvent();

export const counterpartyOfferSucceeded = createEvent();

const confirmFx = createEffect(async ({ actionId }: { actionId: string }) => {
  await ActionService.postApiV1ActionConfirm({
    id: actionId,
    requestBody: { comment: '' },
  });
});

const rejectFx = createEffect(
  async ({
    actionId,
    comment = '',
  }: {
    actionId: string;
    comment?: string;
  }) => {
    await ActionService.postApiV1ActionReject({
      id: actionId,
      requestBody: { comment },
    });
  }
);

const deleteFx = createEffect(async ({ actionId }: { actionId: string }) => {
  await ActionService.postApiV1ActionDelete({
    id: actionId,
    requestBody: { comment: '' },
  });
});

const reworkFx = createEffect(
  async ({ actionId, comment }: { actionId: string; comment: string }) => {
    await ActionService.postApiV1ActionRework({
      id: actionId,
      requestBody: { comment },
    });
  }
);

const editApplicationFx = createEffect(
  async (params: {
    actionId: string;
    applicationId: string;
    contractUniqueNumber: string;
    tradeAgreementNumber: string;
    files: File[];
  }) => {
    await ActionService.postApiV1ActionFormEditApplication({
      id: params.actionId,
      formData: {
        contractUniqueNumber: params.contractUniqueNumber,
        tradeAgreementNumber: params.tradeAgreementNumber,
        newFiles: params.files,
        id: params.applicationId,
      },
    });
  }
);

const editDraftFx = createEffect(
  async ({
    actionId,
    requestBody,
  }: {
    actionId: string;
    requestBody: FormEditDraftActionCommand;
  }) => {
    await ActionService.postApiV1ActionFormEditDraft({
      id: actionId,
      requestBody,
    });
  }
);

const sendForReviewFx = createEffect(
  async ({ actionId }: { actionId: string }) => {
    await ActionService.postApiV1ActionConfirm({
      id: actionId,
      requestBody: { comment: '' },
    });
  }
);

const signFx = createEffect(async ({ actionId }: { actionId: string }) => {
  await ActionService.postApiV1ActionSign({
    id: actionId,
    requestBody: {},
  });
});

const formAgentInfoFx = createEffect(
  async ({
    actionId,
    data,
  }: {
    actionId: string;
    data: FormAgentInfoFormData;
  }) => {
    await ActionService.postApiV1ActionFormAgentInfo({
      id: actionId,
      formData: {
        agentCryptoAddress: data.agentCryptoAddress ?? undefined,
        agentWalletNetId:
          data.agentWalletNetId != null
            ? Number(data.agentWalletNetId)
            : undefined,
        commissionAgentRUB:
          data.commissionAgentRUB != null
            ? Number(data.commissionAgentRUB)
            : undefined,
        commissionAuthorizedOrganizationRUB:
          data.commissionAuthorizedOrganizationRUB != null
            ? Number(data.commissionAuthorizedOrganizationRUB)
            : undefined,
        cryptoAmount:
          data.cryptoAmount != null ? Number(data.cryptoAmount) : undefined,
        cryptoCurrencyId:
          data.cryptoCurrencyId != null
            ? Number(data.cryptoCurrencyId)
            : undefined,
        bankName: data.bankName ?? undefined,
        organizationName: data.organizationName ?? undefined,
        senderCountryId:
          data.senderCountryId != null
            ? Number(data.senderCountryId)
            : undefined,
        files: data.files ?? undefined,
        comment: '',
      },
    });
  }
);

const formCounterpartyInfoFx = createEffect(
  async (params: {
    actionId: string;
    sign?: string;
    commissionAuthorizedOrganizationRUB: number;
    files: File[];
  }) => {
    await ActionService.postApiV1ActionFormCounterpartyInfo({
      id: params.actionId,
      formData: {
        commissionAuthorizedOrganizationRUB:
          params.commissionAuthorizedOrganizationRUB,
        files: params.files,
      },
    });
  }
);

const formActionSucceeded = merge([
  editApplicationFx.doneData,
  editDraftFx.doneData,
  sendForReviewFx.doneData,
  formAgentInfoFx.doneData,
  formCounterpartyInfoFx.doneData,
  signFx.doneData,
]);

sample({
  clock: confirmRequested,
  fn: ({ actionId }) => ({ actionId }),
  target: confirmFx,
});
sample({
  clock: rejectRequested,
  fn: ({ actionId, comment }) => ({ actionId, comment }),
  target: rejectFx,
});
sample({
  clock: deleteRequested,
  fn: ({ actionId }) => ({ actionId }),
  target: deleteFx,
});
sample({
  clock: reworkRequested,
  fn: ({ actionId, comment }) => ({ actionId, comment }),
  target: reworkFx,
});
sample({ clock: editApplicationRequested, target: editApplicationFx });
sample({ clock: editDraftRequested, target: editDraftFx });
sample({ clock: sendForReviewRequested, target: sendForReviewFx });
sample({
  clock: signRequested,
  fn: ({ actionId }) => ({ actionId }),
  target: signFx,
});
sample({
  clock: formAgentInfoRequested,
  fn: ({ actionId, data }) => ({ actionId, data }),
  target: formAgentInfoFx,
});
sample({
  clock: counterpartyOfferRequested,
  target: formCounterpartyInfoFx,
});
sample({
  clock: formCounterpartyInfoFx.doneData,
  target: counterpartyOfferSucceeded,
});

const reviewActionSucceeded = merge([
  confirmFx.doneData,
  rejectFx.doneData,
  deleteFx.doneData,
  reworkFx.doneData,
]);
sample({ clock: reviewActionSucceeded, target: actionSucceeded });
sample({
  clock: formActionSucceeded,
  target: applicationActionSucceeded,
});

const allFailData = merge([
  confirmFx.failData,
  rejectFx.failData,
  deleteFx.failData,
  reworkFx.failData,
  editApplicationFx.failData,
  editDraftFx.failData,
  sendForReviewFx.failData,
  formAgentInfoFx.failData,
  formCounterpartyInfoFx.failData,
  signFx.failData,
]);

export const $actionError = createStore<string | null>(null)
  .on(confirmRequested, () => null)
  .on(rejectRequested, () => null)
  .on(deleteRequested, () => null)
  .on(reworkRequested, () => null)
  .on(editApplicationRequested, () => null)
  .on(editDraftRequested, () => null)
  .on(sendForReviewRequested, () => null)
  .on(signRequested, () => null)
  .on(formAgentInfoRequested, () => null)
  .on(counterpartyOfferRequested, () => null)
  .on(allFailData, (_, e) =>
    e instanceof Error ? e.message : 'Не удалось выполнить действие'
  );

export const $isSubmitting = combine(
  confirmFx.pending,
  rejectFx.pending,
  deleteFx.pending,
  reworkFx.pending,
  editApplicationFx.pending,
  editDraftFx.pending,
  sendForReviewFx.pending,
  formAgentInfoFx.pending,
  formCounterpartyInfoFx.pending,
  signFx.pending,
  (a, b, c, d, e, f, g, h, i, j) =>
    a || b || c || d || e || f || g || h || i || j
);
